import { themeGet } from '@styled-system/theme-get';
import { useTheme } from '@chakra-ui/core';

export type AnyProps = any;

const componentThemeGet = (
    key: string,
    prop: string,
    fallbackProp: string,
    fallbackValue: string = null
) => (props: any): string => {
    let themeProp = prop;
    let result = themeGet(`components.${key}.${prop}`)(props);

    // if result is just a number, then it's an array index value that we need to use
    // to grab the appropriate value based on the property
    if (typeof result !== 'string') {
        if (!/s$/.test(themeProp)) {
            themeProp = `${themeProp}s`;
        }
        result = themeGet(`${themeProp}.${result}`)(props);
    }

    return result || themeGet(fallbackProp, fallbackValue)(props) || '';
};

const componentStyles = (key: string, path: string = null): any => (props: any) => {
    const keypath = path ? `${key}.${path}` : key;
    return themeGet(`components.${keypath}`)(props);
};

const intersection = (ar1: any[], ar2: any[]): any[] => (
    ar1.filter((value) => ar2.indexOf(value) !== -1)
);
export const isNull = (obj: any): boolean => obj === null;
export const isUndefined = (obj: any): boolean => undefined === obj;
export const isset = (obj: any): boolean => !isUndefined(obj) && !isNull(obj);

export const isEmpty = (obj: any): boolean => {
    if (!isset(obj)) return true;
    if (obj == null) return true;
    if (Array.isArray(obj) || typeof obj === 'string') return obj.length === 0;
    return Object.keys(obj).length === 0;
};

export const get = (obj: any, path: string): any => {
    if (typeof obj === 'undefined' || obj === null || !isset(path)) return undefined;
    const props = path.split(/[\.\[\]\"\']{1,2}/);
    const len = props.length;
    for (let i = 0; i < len; i += 1) {
        if (!isEmpty(props[i])) {
            obj = obj[props[i]];
            if (typeof obj === 'undefined' || obj === null) return undefined;
        }
    }
    return obj;
};

export const set = (obj: any, path: string, val: any) => {
    if (!isset(path)) return obj;
    const props = path.split('.');
    const len = props.length - 1;
    let i;
    for (i = 0; i < len; i += 1) {
        obj[props[i]] = obj[props[i]] || {};
        obj = obj[props[i]];
    }
    obj[props[i]] = val;
    return obj;
};

export const debounce = (callback: (...args: any[]) => any, wait: number = 250) => {
    let timeoutId: number;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            timeoutId = null;
            callback(...args);
        }, wait);
    };
};

export const numberWithCommas = (x: any): string => `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export { themeGet, useTheme, componentThemeGet, componentStyles, intersection };
